import { useTranslation } from "react-i18next";
import { stdObject } from "../../../data/types/stdObject";
import BasicButton from "../../BasicButton";
import WriteInput from "../../componentModal/WriteInput";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import { useEffect, useRef, useState } from "react";
import { colors } from "../../../data/colors";

export default function DrawingBox({
  draw,
  setDrawing,
}: {
  draw: stdObject;
  setDrawing: any;
}) {
  const { t } = useTranslation();
  const [pincelWidth, setPincelWidth] = useState(10);
  const [color, setColor] = useState("black");

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);

  // Historial de acciones (para undo/redo)
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const imageData = new Image();
    imageData.onload = () => {
      const parentWidth = canvas.parentElement?.clientWidth || imageData.width;
      const scale = parentWidth / imageData.width;
      canvas.width = parentWidth;
      canvas.height = imageData.height * scale;

      canvas.style.border = `solid 1px ${colors.black}`;

      const context = canvas.getContext("2d");
      if (!context) return;

      context.lineCap = "round";
      context.strokeStyle = colors.black;
      context.lineWidth = 3;

      context.drawImage(imageData, 0, 0, canvas.width, canvas.height);
      contextRef.current = context;

      saveState();
    };
    imageData.src = draw.data ?? draw.original;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;
    context.strokeStyle = color;
    context.lineWidth = (pincelWidth * 3) / 10;
    contextRef.current = context;
  }, [color, pincelWidth]);

  const saveState = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL();
    setDrawing([draw.original, draw.data].includes(dataUrl) ? "" : dataUrl);
    setUndoStack((prev) => [...prev, dataUrl]);
    setRedoStack([]); // Limpia el redo cada vez que se dibuja algo nuevo
  };

  const restoreState = (dataUrl: string) => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context) return;

    const image = new Image();
    image.src = dataUrl;
    image.onload = () => {
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);
    };
  };

  const startDrawing = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const { offsetX, offsetY } = event.nativeEvent;
    const context = contextRef.current;
    if (!context) return;

    context.beginPath();
    context.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const drawAction = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const { offsetX, offsetY } = event.nativeEvent;
    const context = contextRef.current;
    if (!context) return;

    context.lineTo(offsetX, offsetY);
    context.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    const context = contextRef.current;
    if (!context) return;

    context.closePath();
    setIsDrawing(false);
    saveState();
  };

  const handleUndo = () => {
    if (undoStack.length <= 1) return;

    const newUndoStack = [...undoStack];
    const lastState = newUndoStack.pop()!;
    setUndoStack(newUndoStack);
    setRedoStack((prev) => [lastState, ...prev]);

    restoreState(newUndoStack[newUndoStack.length - 1]);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;

    const newRedoStack = [...redoStack];
    const nextState = newRedoStack.shift()!;
    setRedoStack(newRedoStack);
    setUndoStack((prev) => [...prev, nextState]);

    restoreState(nextState);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context) return;
    const imageData = new Image();
    imageData.src = draw.original;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(imageData, 0, 0, canvas.width, canvas.height);
    saveState(); // Guarda el estado vacío para permitir Undo
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div className="drawingRow">
        <WriteInput
          data={{
            type: "range",
            rangeParams: {
              min: 1,
              max: 100,
              value: pincelWidth,
              step: 1,
            },
            onChange: (v: stdObject) => setPincelWidth(v.target.value),
            inputStyle: { width: "auto" },
          }}
        />
        <div
          style={{
            position: "relative",
            borderRadius: 15,
            height: 30,
            width: 30,
            justifyItems: "center",
            alignContent: "center",
            backgroundColor: colors.lightGrey,
            border: `solid 1px ${colors.grey}`,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              marginLeft: -((pincelWidth * 3) / 10 / 2) + 0.5,
              marginTop: -((pincelWidth * 3) / 10 / 2) + 0.5,
              borderRadius: 15,
              height: (pincelWidth * 3) / 10,
              width: (pincelWidth * 3) / 10,
              backgroundColor: color,
            }}
          />
        </div>
        <WriteInput
          data={{
            type: "color",
            value: color,
            inputStyle: { width: 50 },
            onChange: (v: stdObject) => setColor(v.target.value),
          }}
        />
      </div>
      <div className="drawingRow">
        <BasicButton
          title={t("p.eraser")}
          Icon={CleaningServicesIcon}
          onClick={handleClear}
        />
        <BasicButton title={t("p.undo")} Icon={UndoIcon} onClick={handleUndo} />
        <BasicButton title={t("p.redo")} Icon={RedoIcon} onClick={handleRedo} />
      </div>
      <canvas
        ref={canvasRef}
        onMouseDown={startDrawing}
        onMouseMove={drawAction}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
      />
    </div>
  );
}
